/** @jsxImportSource @emotion/react */
import React from "react";
import * as authSelectors from "../auth/authSelectors";
import { css } from "@emotion/react";
import { colors } from "../../components/colors";
import { useSelector } from "react-redux";
import { StyledText } from "../../assets/typography/typography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { Dropdown, MenuProps } from "antd";
import { CircleContainer } from "../../components/CircleContainer";
import { TopbarDropdownOrganizationItem } from "./TopbarDropdownOrganizationItem";
import { TopBarDropdownMenu } from "./TopBarDropdownMenu";
import { HeaderBrand } from "./HeaderBrand";
import { Organization } from "../../model/Organization";
import { CountryDropdown } from "./CountryDropdown";
import { breakpoints } from "../../components/breakpoints";

export const localStorageCountrySpecificValues = "countrySpecificValues";
export const vatPercentages = new Map([
  ["NO", 15],
  ["DK", 25],
  ["IS", 11],
  ["GL", 0],
  ["BE", 21],
  ["FR", 20],
  ["DE", 19],
]);

export const timezones = new Map([
  ["NO", "Europe/Oslo"],
  ["DK", "Europe/Copenhagen"],
  ["IS", "Atlantic/Reykjavik"],
  ["GL", "America/Nuuk"],
  ["BE", "Europe/Brussels"],
  ["FR", "Europe/Paris"],
  ["DE", "Europe/Berlin"],
]);

const rightContentWrapper = css`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const circleContainerAdditionalStyling = css`
  border: 1px solid ${colors.ctaInactive};
`;

const topbarWrapper = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid ${colors.ctaInactive};
  background-color: ${colors.white};
  padding-top: 12px;
  padding-bottom: 12px;
  padding-left: 24px;
  padding-right: 24px;
`;

const contentWrapperStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 1200px;
`;

const profileContainerStyle = css`
  display: flex;
  flex-direction: row;
  cursor: pointer;
  gap: 12px;
  align-items: center;
`;

const countryDropdownStyle = css`
  ${breakpoints.mobile} {
    display: none;
  }
`;

type Props = {
  onOrganizationSelect: (organizationkeyName: string) => void;
  toggleMenuDropdown: () => void;
  logOut: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

export const OrganizationTopbarSection = ({
  onOrganizationSelect,
  toggleMenuDropdown,
  logOut,
}: Props) => {
  const organization = useSelector(authSelectors.selectedOrganization);
  const organizations = useSelector(authSelectors.organizations);
  const userDisplayName = useSelector(authSelectors.name);
  const orgs: MenuProps["items"] = organizations
    .sort((a: Organization, b: Organization) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    })
    .map((o) => ({
      key: o.keyName,
      label: (
        <TopbarDropdownOrganizationItem
          organization={o}
          chosenOrganizationName={organization?.keyName}
          onOrganizationSelect={onOrganizationSelect}
        />
      ),
    }));
  const items: MenuProps["items"] = [
    {
      key: "1",
      type: "group",
      label: (
        <StyledText
          css={css`
            margin-bottom: 4px;
          `}
          color={colors.textSecondary}
          className="bodySubtextSemibold"
        >
          Organizations
        </StyledText>
      ),
      children: orgs,
    },
  ];

  return (
    <div css={topbarWrapper}>
      <div css={contentWrapperStyle}>
        <HeaderBrand organizationName={organization?.name} />
        <div css={rightContentWrapper}>
          <div css={countryDropdownStyle}>
            <CountryDropdown showVerticalDivider={true} />
          </div>
          <Dropdown
            menu={{
              items: items,
              selectable: true,
              defaultSelectedKeys: [organization?.keyName ?? ""],
            }}
            placement="bottomRight"
            dropdownRender={(menu) => (
              <TopBarDropdownMenu
                menu={menu}
                userDisplayName={userDisplayName}
                logOut={logOut}
              />
            )}
          >
            <div css={profileContainerStyle}>
              <CircleContainer
                width="40px"
                height="40px"
                backgroundColor={colors.white}
                additionalStyling={circleContainerAdditionalStyling}
              >
                <CircleContainer
                  width="32px"
                  height="32px"
                  backgroundColor={colors.ctaInactive}
                >
                  <StyledText className="bodyTextSemibold mb-0">
                    <FontAwesomeIcon icon={solid("user")} />
                  </StyledText>
                </CircleContainer>
              </CircleContainer>
            </div>
          </Dropdown>
          <button
            className="btn btn-link header-toggler d-lg-none ml-3 ml-lg-0"
            data-toggle="collapse"
            data-target="#headerMenuCollapse"
            aria-expanded="true"
            onClick={toggleMenuDropdown}
          >
            <span className="header-toggler-icon" />
          </button>
        </div>
      </div>
    </div>
  );
};
